import React, { useRef, useState } from "react";
import { Box, Card, CardContent, Chip, TableCell, Tooltip, Typography } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import LocalPostOfficeIcon from "@mui/icons-material/LocalPostOffice";
import PhoneIcon from "@mui/icons-material/Phone";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import SmsIcon from "@mui/icons-material/Sms";
import { Customer } from "../../../../types/Customer";

export type TabPanelProps = { children?: React.ReactNode; value: number; index: number };

export type IdentifierRaw = {
    PERSON_ID?: string;
    PERSON_IDENTIFIER_ID?: string;
    PERSON_IDENTIFIER_TYPE_ID?: string;
    PERSON_IDENTIFIER_TYPE_NAME?: string;
    IDENTIFIER_VALUE?: string;
    PRIORITY?: string;
    ACTIVATION_DATE?: string;
    END_VALIDITY_DATE?: string | null;
    DESACTIVATION_DATE?: string | null;
    FIRST_ACTIVITY_DATE?: string;
    LAST_ACTIVITY_DATE?: string;
};

export type AccountRaw = {
    ACCOUNT_HEAD_NAME?: string;
    ACCOUNT_HEAD_UNIT?: string;
    VALUE?: number | string;
    UNIT?: string;
    ACCOUNT_HEAD_ID?: string;
    ACCOUNT_ID?: string | number;
};

export type SubscriptionRaw = { PER_PERSON_SUBSCRIPTIONS_ID?: string; IS_SUBSCRIBED?: string; LABEL?: string; UPDATED_AT?: string };
export type EventRaw = { CATEGORY_NAME?: string; NAME?: string; EVENT_DATE?: string };
export type BadgeRaw = {
    SEGMENT_NAME?: string;
    SEGMENT_DESCRIPTION?: string;
    IMG?: string;
    CUSTOMER_VISIBLE?: string;
    QUERY_VISIBLE?: string;
    CATEGORY_NAME?: string;
    CATEGORY_DESCRIPTION?: string;
    TYPE_ID?: string;
    UPDATE_DATE?: string;
};
export type GroupMemberRaw = {
    SHORT_TITLE?: string;
    FIRSTNAME?: string;
    LASTNAME?: string;
    PERSON_ID?: string | number;
    IDENTIFIER_VALUE?: string;
    BIRTH_DATE?: string;
    IS_MAIN_MEMBER?: string;
    UPDATE_DATE?: string;
};

export type RealtimeOffer = {
    offerId?: string | number;
    offerName?: string;
    offerDescription?: string;
    objApplication?: string;
    startDate?: string;
    sendDate?: string;
    endDate?: string;
    offerNumber?: string | number;
    used?: boolean;
    [key: string]: unknown;
};

export type Offer = {
    NUMOPERATION?: string;
    SENTDATE?: string;
    TRDATE?: string;
    OPERATIONNAME?: string;
    OFFERDESCRIPTION?: string;
    CANAL?: unknown;
    STARTDATE?: string;
    ENDDATE?: string;
    ISUSED?: boolean | string | number;
    [key: string]: unknown;
};

export type TxDetails = {
    transaction_items?: {
        LIBELLE?: string;
        REFERENCE?: string;
        QUANTITE?: string | number;
        TTC?: string | number;
        REMISE?: string | number;
    }[];
    transaction_offers?: {
        NUMOPERATION?: string;
        SENTDATE?: string;
        OPERATIONNAME?: string;
        OFFERDESCRIPTION?: string;
        CANAL?: unknown;
        STARTDATE?: string;
        ENDDATE?: string;
    }[];
};

export type EditForm = {
    title_id: string; lastname: string; firstname: string; birthdate: string;
    address1: string; address2: string; address3_num: string; address3_type: string; address3_name: string;
    address4: string; zip_code: string; local_city_name: string; local_country_name: string;
    email_address: string; cell_phone_number: string; phone_number: string;
    mailCompany: boolean; emailCompany: boolean; smsCompany: boolean;
    mailTrusted: boolean; emailTrusted: boolean; smsTrusted: boolean;
    reason: string;
    storeId: string;
};

export type MvtForm = { accountId: string; moveDate: string; value: string; cause: string; media: string };

export type ManualTransacForm = {
    personIdentifierId: string;
    personId: string;
    entityId: string;
    dateTransaction: string;
    amount: string;
    currencyId: string;
    cashDeskNumber: string;
    receiptNumber: string;
    offers: string;
    motif: string;
    media: string;
};

export const formatAmount = (raw: number | string | null | undefined): string => {
    if (raw == null || raw === "") return "—";
    if (typeof raw === "number") return isNaN(raw) ? "—" : `${raw.toFixed(2)} €`;
    const num = parseFloat(raw.replace(/€/g, "").replace(/\s/g, "").replace(",", "."));
    return isNaN(num) ? "—" : `${num.toFixed(2)} €`;
};

export const formatDate = (val: string | null | undefined): string => {
    if (!val) return "—";
    const dateOnly = val.replace(/[T ][\d:]+.*$/, "").trim();
    // ISO yyyy-mm-dd → dd/mm/yyyy
    const isoMatch = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
    // Oracle dd/mm/yy (2-digit year) → dd/mm/20yy
    const shortMatch = dateOnly.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
    if (shortMatch) return `${shortMatch[1]}/${shortMatch[2]}/20${shortMatch[3]}`;
    return dateOnly;
};

export const parseDMY = (d?: string | null): number => {
    if (!d) return 0;
    const p = d.split("/");
    return p.length === 3 ? new Date(+p[2], +p[1] - 1, +p[0]).getTime() : 0;
};

export interface AccountAmount {
    value: string;
    head: string;
}

const collectIndexedAccounts = (tx: Record<string, unknown>, valueKey: string, headKey: string): AccountAmount[] => {
    const results: AccountAmount[] = [];
    let i = 0;
    while (tx[`${valueKey}${i}`] !== undefined) {
        results.push({ value: String(tx[`${valueKey}${i}`] ?? ""), head: String(tx[`${headKey}${i}`] ?? `Compte ${i}`) });
        i++;
    }
    return results;
};

export const getAccValues = (tx: Record<string, unknown>): AccountAmount[] => collectIndexedAccounts(tx, "ACCOUNTVALUE", "ACCOUNTHEADVALUE");

export const getAccCumul = (tx: Record<string, unknown>): AccountAmount[] => collectIndexedAccounts(tx, "ACCOUNTCUMUL", "ACCOUNTHEADCUMUL");

// Un compte à 0 n'est pas pertinent pour la transaction (elle ne mouvemente qu'un seul type de compteur à la fois) : on l'ignore pour n'afficher que la valeur qui compte.
export const pickRelevantAccounts = (accounts: AccountAmount[]): AccountAmount[] => {
    const nonZero = accounts.filter(acc => parseFloat(acc.value) !== 0);
    return nonZero.length > 0 ? nonZero : accounts;
};

export const formatDateWithHours = (val: string | null | undefined): string => {
    if (!val) return "—";
    const s = val.trim();
    const timeMatch = s.match(/[T ](\d{2}:\d{2}(?::\d{2})?)/);
    const time = timeMatch ? timeMatch[1] : null;
    const dateOnly = s.replace(/[T ][\d:]+.*$/, "").trim();
    // ISO yyyy-mm-dd → dd/mm/yyyy
    const isoMatch = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
        const date = `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
        return time ? `${date} ${time}` : date;
    }
    // Oracle dd/mm/yy (2-digit year) → dd/mm/20yy
    const shortMatch = dateOnly.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
    if (shortMatch) {
        const date = `${shortMatch[1]}/${shortMatch[2]}/20${shortMatch[3]}`;
        return time ? `${date} ${time}` : date;
    }
    return time ? `${dateOnly} ${time}` : dateOnly;
};

export const canalKey = (canal: unknown): string => {
    if (!canal) return "";
    const s = String(canal);
    const match = s.match(/\/([^/]+?)(?:_small)?\.png$/i);
    return match ? match[1].toLowerCase() : s.toLowerCase();
};

export const buildFullName = (customer: Customer): string => {
    const per = (customer as Record<string, unknown>).PER_PERSON as Record<string, unknown> | undefined;
    return [per?.CIVILITY ?? customer.CIVILITY, per?.FIRSTNAME ?? customer.FIRSTNAME, per?.LASTNAME ?? customer.LASTNAME]
        .filter(Boolean).join(" ") || `Client #${customer.PERSONID}`;
};

const clean = (val: unknown): string | null => {
    const s = String(val ?? "").trim();
    return !s || s === "." || s === "-" ? null : s;
};

export const buildAddress = (customer: Customer): string =>
    [clean(customer.ADDRESS1), clean(customer.ADDRESS2), clean(customer.ADDRESS3), clean(customer.ADDRESS4),
     [clean(customer.ZIP_CODE), clean(customer.CITY)].filter(Boolean).join(" ") || null, clean(customer.COUNTRY)]
        .filter(Boolean).join(", ");

export const toArray = <T,>(raw: unknown): T[] =>
    Array.isArray(raw) ? raw as T[]
    : raw && typeof raw === "object" ? Object.values(raw) as T[]
    : [];

const CANAL_CONFIG: Record<string, { icon: React.ReactNode; label: string; palette: { bg: string; color: string } }> = {
    // Labels texte
    email:    { icon: <EmailIcon />,           label: "Email",          palette: { bg: "#e3f2fd", color: "#1565c0" } },
    mail:     { icon: <EmailIcon />,           label: "Email",          palette: { bg: "#e3f2fd", color: "#1565c0" } },
    emailing: { icon: <EmailIcon />,           label: "Emailing",       palette: { bg: "#e3f2fd", color: "#1565c0" } },
    e:        { icon: <EmailIcon />,           label: "Email",          palette: { bg: "#e3f2fd", color: "#1565c0" } },
    sms:      { icon: <SmsIcon />,             label: "SMS",            palette: { bg: "#e8f5e9", color: "#2e7d32" } },
    mobile:   { icon: <SmartphoneIcon />,      label: "Mobile",         palette: { bg: "#e8f5e9", color: "#2e7d32" } },
    tikeo:    { icon: <SmartphoneIcon />,      label: "Mobile (Tikéo)", palette: { bg: "#e8f5e9", color: "#2e7d32" } },
    push:     { icon: <SmartphoneIcon />,      label: "Push",           palette: { bg: "#e8f5e9", color: "#2e7d32" } },
    courrier: { icon: <LocalPostOfficeIcon />, label: "Courrier",       palette: { bg: "#fff3e0", color: "#e65100" } },
    postal:   { icon: <LocalPostOfficeIcon />, label: "Courrier",       palette: { bg: "#fff3e0", color: "#e65100" } },
    mailing:  { icon: <LocalPostOfficeIcon />, label: "Mailing",        palette: { bg: "#fff3e0", color: "#e65100" } },
    m:        { icon: <LocalPostOfficeIcon />, label: "Mailing",        palette: { bg: "#fff3e0", color: "#e65100" } },
    cashdesk: { icon: <PointOfSaleIcon />,     label: "Caisse",         palette: { bg: "#f3e5f5", color: "#6a1b9a" } },
    caisse:   { icon: <PointOfSaleIcon />,     label: "Caisse",         palette: { bg: "#f3e5f5", color: "#6a1b9a" } },
    tel:      { icon: <PhoneIcon />,           label: "Tél",            palette: { bg: "#f3e5f5", color: "#6a1b9a" } },
    phone:    { icon: <PhoneIcon />,           label: "Tél",            palette: { bg: "#f3e5f5", color: "#6a1b9a" } },
    web:      { icon: <LanguageIcon />,        label: "Web",            palette: { bg: "#e1f5fe", color: "#01579b" } },
    internet: { icon: <LanguageIcon />,        label: "Web",            palette: { bg: "#e1f5fe", color: "#01579b" } },
    // IDs numériques (courrier=1, email=2, sms=3, tikeo=4, cashdesk=5)
    "1": { icon: <LocalPostOfficeIcon />, label: "Courrier",       palette: { bg: "#fff3e0", color: "#e65100" } },
    "2": { icon: <EmailIcon />,           label: "Email",          palette: { bg: "#e3f2fd", color: "#1565c0" } },
    "3": { icon: <SmsIcon />,             label: "SMS",            palette: { bg: "#e8f5e9", color: "#2e7d32" } },
    "4": { icon: <SmartphoneIcon />,      label: "Mobile (Tikéo)", palette: { bg: "#e8f5e9", color: "#2e7d32" } },
    "5": { icon: <PointOfSaleIcon />,     label: "Caisse",         palette: { bg: "#f3e5f5", color: "#6a1b9a" } },
};

const DEFAULT_PALETTE = { bg: "#f5f5f5", color: "#616161" };

export const headerCellSx = { fontWeight: 600, fontSize: "0.72rem" as const, color: "text.primary", whiteSpace: "nowrap" as const, overflow: "hidden" as const, textOverflow: "ellipsis" as const, py: 1.5 };
export const dataCellSx   = { overflow: "hidden" as const, textOverflow: "ellipsis" as const, whiteSpace: "nowrap" as const, fontSize: "0.75rem" as const, py: 1.5 };

export const TabPanel = ({ children, value, index }: TabPanelProps) => (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
        {value === index && children}
    </Box>
);

export const CanalChip = ({ canal }: { canal: unknown }) => {
    if (!canal) return <span style={{ color: "#bbb" }}>—</span>;
    const key = canalKey(canal);
    const conf = CANAL_CONFIG[key];
    const label = conf?.label ?? (key || String(canal)).toUpperCase();
    const p = conf?.palette ?? DEFAULT_PALETTE;
    return (
        <Tooltip title={label} arrow>
            <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: "50%", bgcolor: p.bg, color: p.color, border: `1.5px solid ${p.color}30`, cursor: "default", flexShrink: 0, "& svg": { fontSize: 16 } }}>
                {conf?.icon ?? <span style={{ fontSize: 10, fontWeight: 700, lineHeight: 1 }}>{label.charAt(0)}</span>}
            </Box>
        </Tooltip>
    );
};

export const TooltipCell = ({
    label, sx, children,
}: {
    label?: string | null;
    sx?: object;
    children: React.ReactNode;
}) => {
    const cellRef = useRef<HTMLTableCellElement>(null);
    const [tooltipEnabled, setTooltipEnabled] = useState(false);

    const handleMouseEnter = () => {
        const el = cellRef.current;
        if (el) {
            // Cherche le premier nœud texte enfant tronqué
            const inner = el.querySelector<HTMLElement>("[data-truncate]") ?? el;
            setTooltipEnabled(inner.scrollWidth > inner.clientWidth);
        }
    };

    const tip = label ?? (typeof children === "string" ? children : "");

    return (
        <Tooltip
            title={tooltipEnabled && tip && tip !== "—" ? tip : ""}
            arrow
            placement="top"
            disableHoverListener={false}
        >
            <TableCell
                ref={cellRef}
                sx={{ ...dataCellSx, ...sx }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setTooltipEnabled(false)}
            >
                <Box
                    data-truncate
                    component="span"
                    sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                    {children}
                </Box>
            </TableCell>
        </Tooltip>
    );
};

export const TooltipHeaderCell = ({
    sx, children,
}: {
    sx?: object;
    children: React.ReactNode;
}) => {
    const cellRef = useRef<HTMLTableCellElement>(null);
    const [tooltipEnabled, setTooltipEnabled] = useState(false);

    const handleMouseEnter = () => {
        const el = cellRef.current;
        if (el) {
            const inner = el.querySelector<HTMLElement>("[data-truncate]") ?? el;
            setTooltipEnabled(inner.scrollWidth > inner.clientWidth);
        }
    };

    const tip = typeof children === "string" ? children : "";

    return (
        <Tooltip
            title={tooltipEnabled && tip ? tip : ""}
            arrow
            placement="top"
        >
            <TableCell
                ref={cellRef}
                sx={{ ...headerCellSx, ...sx }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setTooltipEnabled(false)}
            >
                <Box
                    data-truncate
                    component="span"
                    sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                    {children}
                </Box>
            </TableCell>
        </Tooltip>
    );
};

export const InfoRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string | number | null }) => {
    if (!value) return null;
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
            <Box sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}>{icon}</Box>
            <Box>
                <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                <Typography variant="body2" fontWeight={500}>{String(value)}</Typography>
            </Box>
        </Box>
    );
};

export const InfoCard = ({ title, children, icon, noPad = false }: { title: string; children: React.ReactNode; icon?: React.ReactNode; noPad?: boolean }) => (
    <Card variant="outlined" sx={{ height: "100%", overflow: "hidden", borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 2, py: 1, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}>
            {icon && <Box sx={{ color: "primary.main", display: "flex", opacity: 0.8, "& svg": { fontSize: 15 } }}>{icon}</Box>}
            <Typography variant="caption" fontWeight={700} sx={{ color: "text.secondary", letterSpacing: "0.6px", textTransform: "uppercase", fontSize: "0.7rem" }}>
                {title}
            </Typography>
        </Box>
        <CardContent sx={{ p: noPad ? 0 : 2, pt: noPad ? 0 : 1.5, "&:last-child": { pb: noPad ? 0 : 2 } }}>
            {children}
        </CardContent>
    </Card>
);

export const OverflowTooltip = ({ value, sx, ...typographyProps }: { value: string; sx?: React.CSSProperties | object } & Omit<React.ComponentProps<typeof Typography>, "ref" | "children">) => {
    const ref = useRef<HTMLElement>(null);
    const [overflow, setOverflow] = useState(false);
    const handleMouseEnter = () => {
        if (ref.current) setOverflow(ref.current.scrollWidth > ref.current.clientWidth);
    };
    return (
        <Tooltip title={overflow ? value : ""} arrow>
            <Typography ref={ref} noWrap onMouseEnter={handleMouseEnter} sx={{ overflow: "hidden", textOverflow: "ellipsis", ...sx }} {...typographyProps}>
                {value}
            </Typography>
        </Tooltip>
    );
};

export const DialogSection = ({ icon, label }: { icon?: React.ReactNode; label: string }) => (
    <Box sx={{ px: 2, py: 0.6, bgcolor: "grey.50", borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 0.75 }}>
        {icon && <Box sx={{ color: "primary.main", opacity: 0.8, display: "flex", "& svg": { fontSize: 13 } }}>{icon}</Box>}
        <Typography variant="caption" fontWeight={700} sx={{ color: "text.secondary", letterSpacing: "0.6px", textTransform: "uppercase", fontSize: "0.7rem" }}>{label}</Typography>
    </Box>
);
